/**
 * PND.50 — หนังสือรับรองการหักภาษี ณ ที่จ่าย ตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร
 * Matches the official Thai Revenue Department form layout (A4 portrait)
 * Opens in a new window for Print / Save as PDF
 */

export interface PND50EmployeeInput {
  certificateNumber: string
  year: number
  employeeNameTh: string
  employeeName: string
  employeeNationalId: string
  employeeAddress: string
  totalIncome: number
  totalTax: number
  companyTaxId?: string
  incomeYear?: number // BE year for the income, defaults to year+543
  payDate?: string // e.g. "25/3/2569"
}

function toBE(ce: number): number { return ce + 543 }

function fmtAmt(n: number): string {
  if (!n && n !== 0) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function taxIdBoxes(id: string): string {
  const d = id.replace(/\D/g, '')
  if (d.length === 13) {
    // Format: X - XXXX - XXXXX - XX - X (1-4-5-2-1 grouping)
    const groups = [d[0], d.slice(1,5), d.slice(5,10), d.slice(10,12), d[12]]
    return groups.map((g, gi) =>
      g.split('').map(c => `<span class="tbox">${c}</span>`).join('') +
      (gi < groups.length - 1 ? '<span class="tbox-sep">-</span>' : '')
    ).join('')
  }
  return d.split('').map(c => `<span class="tbox">${c}</span>`).join('')
}

export async function generateMailBITPND50(input: PND50EmployeeInput): Promise<void> {
  const be = input.incomeYear || toBE(input.year)
  const companyTaxId = input.companyTaxId || '0105549080337'
  const payDate = input.payDate || `${be}`
  const certNo = input.certificateNumber

  const html = `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8">
<title>PND.50 - ${input.employeeName} - ${input.year}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap');
@page { size: A4 portrait; margin: 10mm 12mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Sarabun', 'TH SarabunPSK', 'Tahoma', sans-serif; font-size: 13px; line-height: 1.35; color: #000; }
.page { width: 100%; max-width: 190mm; margin: 0 auto; border: 2px solid #000; padding: 6mm; }

/* Top labels */
.copy-labels { font-size: 11px; margin-bottom: 2px; }
.copy-labels div { margin-bottom: 1px; }

/* Title */
.form-title { text-align: center; margin: 4px 0 2px 0; }
.form-title h1 { font-size: 18px; font-weight: 700; }
.form-title .sub { font-size: 13px; }

/* Cert number top-right */
.cert-header { display: flex; justify-content: space-between; align-items: flex-start; }
.cert-right { text-align: right; font-size: 12px; }
.cert-right div { margin-bottom: 1px; }

/* Tax ID boxes */
.tbox { display: inline-block; width: 16px; height: 18px; border: 1px solid #000; text-align: center; line-height: 18px; font-size: 13px; font-weight: 600; font-family: monospace; vertical-align: middle; }
.tbox-sep { display: inline-block; width: 8px; text-align: center; font-weight: bold; vertical-align: middle; }

/* Sections */
.section { border: 1.5px solid #000; padding: 4px 6px; margin-bottom: 4px; }
.section-title { font-weight: 700; font-size: 13px; margin-bottom: 2px; }
.section-row { display: flex; gap: 6px; margin-bottom: 2px; font-size: 12px; }
.section-row .lbl { font-weight: 600; white-space: nowrap; }
.section-row .val { flex: 1; }
.section-row .hint { font-size: 10px; color: #555; }
.tax-id-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
.tax-id-label { font-size: 11px; font-weight: 600; }

/* Form type */
.form-type-section { border: 1.5px solid #000; padding: 4px 6px; margin-bottom: 4px; font-size: 11.5px; }
.form-type-row { display: flex; flex-wrap: wrap; gap: 4px 12px; }
.cb { display: inline-flex; align-items: center; gap: 2px; }
.cb-box { display: inline-block; width: 14px; height: 14px; border: 1px solid #000; text-align: center; line-height: 14px; font-size: 11px; vertical-align: middle; }
.cb-checked { font-weight: bold; }

/* Income table */
.income-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; font-size: 11.5px; }
.income-table th, .income-table td { border: 1px solid #000; padding: 2px 4px; vertical-align: top; }
.income-table th { background: #f5f5f5; font-weight: 700; text-align: center; font-size: 11px; }
.income-table .col-desc { width: 52%; }
.income-table .col-date { width: 14%; text-align: center; }
.income-table .col-amt { width: 17%; text-align: right; font-family: monospace; font-size: 12px; }
.income-table .total-row { background: #f9f9f9; }
.income-table .total-row td { font-weight: 700; }
.r { text-align: right; font-family: monospace; }
.c { text-align: center; }

/* Bottom sections */
.tax-words { font-size: 12px; margin: 3px 0; border: 1px solid #000; padding: 3px 6px; }
.fund-row { font-size: 11px; margin: 3px 0; }
.payer-row { font-size: 12px; margin: 4px 0; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.bottom-section { display: flex; gap: 8px; margin-top: 6px; font-size: 11px; }
.bottom-left { flex: 1; }
.bottom-right { flex: 1.2; }
.warning-box { border: 1px solid #000; padding: 4px; font-size: 10px; }
.cert-box { border: 1px solid #000; padding: 4px; font-size: 11px; }
.sig-line { margin-top: 8px; }
.note-section { margin-top: 6px; font-size: 10px; color: #333; }

/* Print */
@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .no-print { display: none !important; }
}
.print-bar { position: fixed; top: 0; left: 0; right: 0; background: #1a237e; color: #fff; padding: 8px 16px; display: flex; justify-content: space-between; align-items: center; font-family: sans-serif; z-index: 100; }
.print-bar button { padding: 6px 20px; background: #fff; color: #1a237e; border: none; border-radius: 4px; font-size: 14px; font-weight: 600; cursor: pointer; }
.print-bar button:hover { background: #e8eaf6; }
.print-spacer { height: 44px; }
</style>
</head>
<body>
<div class="print-bar no-print">
  <span>PND.50 — ${input.employeeName} (${input.year})</span>
  <button onclick="window.print()">Print / Save as PDF</button>
</div>
<div class="print-spacer no-print"></div>

<div class="page">
  <!-- Copy labels -->
  <div class="copy-labels">
    <div><strong>ฉบับที่ 1</strong> (สำหรับผู้ถูกหักภาษี ณ ที่จ่าย ใช้แนบพร้อมกับแบบแสดงรายการภาษี)</div>
    <div><strong>ฉบับที่ 2</strong> (สำหรับผู้ถูกหักภาษี ณ ที่จ่าย เก็บไว้เป็นหลักฐาน)</div>
  </div>

  <!-- Title + Cert number -->
  <div class="cert-header">
    <div class="form-title" style="flex:1; text-align:center;">
      <h1>หนังสือรับรองการหักภาษี ณ ที่จ่าย</h1>
      <div class="sub">ตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร</div>
    </div>
    <div class="cert-right">
      <div>เล่มที่ ..........</div>
      <div>เลขที่ <strong>${certNo}</strong></div>
    </div>
  </div>

  <!-- EMPLOYER SECTION -->
  <div class="section">
    <div class="section-title">ผู้มีหน้าที่หักภาษี ณ ที่จ่าย : -</div>
    <div class="tax-id-row">
      <span class="tax-id-label">เลขประจำตัวผู้เสียภาษีอากร (13 หลัก)*</span>
      ${taxIdBoxes(companyTaxId)}
    </div>
    <div class="section-row">
      <span class="lbl">ชื่อ</span>
      <span class="val">บริษัท เมลบิท เอเซีย แปซิฟิค จำกัด (สำนักงานใหญ่)</span>
    </div>
    <div class="section-row" style="font-size:11px; color:#333;">
      <span class="lbl"></span>
      <span class="val">MailBIT Asia Pacific Co., Ltd. (Head Office)</span>
    </div>
    <div class="section-row">
      <span class="lbl">ที่อยู่</span>
      <span class="val">919/252 อาคารจิวเวลรี่ เทรด เซ็นเตอร์ ชั้น 20 ถนนสีลม แขวงสีลม เขตบางรัก กรุงเทพมหานคร 10500</span>
    </div>
  </div>

  <!-- EMPLOYEE SECTION -->
  <div class="section">
    <div class="section-title">ผู้ถูกหักภาษี ณ ที่จ่าย : -</div>
    <div class="tax-id-row">
      <span class="tax-id-label">เลขประจำตัวผู้เสียภาษีอากร (13 หลัก)*</span>
      ${taxIdBoxes(input.employeeNationalId)}
    </div>
    <div class="section-row">
      <span class="lbl">ชื่อ</span>
      <span class="val">${input.employeeNameTh}</span>
    </div>
    <div class="section-row" style="font-size:11px; color:#333;">
      <span class="lbl"></span>
      <span class="val">${input.employeeName}</span>
    </div>
    <div class="section-row">
      <span class="lbl">ที่อยู่</span>
      <span class="val">${input.employeeAddress}</span>
    </div>
  </div>

  <!-- FORM TYPE -->
  <div class="form-type-section">
    <div style="display:flex; gap:12px; align-items:center; flex-wrap:wrap;">
      <span><strong>ลำดับที่</strong> <span class="tbox" style="width:30px;"></span></span>
      <span><strong>ในแบบ</strong></span>
      <span class="cb"><span class="cb-box cb-checked">&#10003;</span> (1) ภ.ง.ด.1ก</span>
      <span class="cb"><span class="cb-box"></span> (2) ภ.ง.ด.1ก พิเศษ</span>
      <span class="cb"><span class="cb-box"></span> (3) ภ.ง.ด.2</span>
      <span class="cb"><span class="cb-box"></span> (4) ภ.ง.ด.3</span>
    </div>
    <div style="display:flex; gap:12px; margin-top:2px; margin-left:120px; flex-wrap:wrap;">
      <span class="cb"><span class="cb-box"></span> (5) ภ.ง.ด.2ก</span>
      <span class="cb"><span class="cb-box"></span> (6) ภ.ง.ด.3ก</span>
      <span class="cb"><span class="cb-box"></span> (7) ภ.ง.ด.53</span>
    </div>
  </div>

  <!-- INCOME TABLE -->
  <table class="income-table">
    <thead>
      <tr>
        <th class="col-desc">ประเภทเงินได้พึงประเมินที่จ่าย</th>
        <th class="col-date">วัน เดือน<br/>หรือปีภาษี ที่จ่าย</th>
        <th class="col-amt">จำนวนเงินที่จ่าย</th>
        <th class="col-amt">ภาษีที่หัก<br/>และนำส่งไว้</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>1. เงินเดือน ค่าจ้าง เบี้ยเลี้ยง โบนัส ฯลฯ ตามมาตรา 40 (1)</td>
        <td class="c">${payDate}</td>
        <td class="r">${fmtAmt(input.totalIncome)}</td>
        <td class="r">${fmtAmt(input.totalTax)}</td>
      </tr>
      <tr><td>2. ค่าธรรมเนียม ค่านายหน้า ฯลฯ ตามมาตรา 40 (2)</td><td></td><td></td><td></td></tr>
      <tr><td>3. ค่าแห่งลิขสิทธิ์ ฯลฯ ตามมาตรา 40 (3)</td><td></td><td></td><td></td></tr>
      <tr>
        <td>4. (ก) ดอกเบี้ย ฯลฯ ตามมาตรา 40 (4) (ก)</td><td></td><td></td><td></td>
      </tr>
      <tr>
        <td style="padding-left:16px;">(ข) เงินปันผล เงินส่วนแบ่งกำไร ฯลฯ ตามมาตรา 40 (4) (ข)</td><td></td><td></td><td></td>
      </tr>
      <tr>
        <td style="padding-left:24px; font-size:10px;">
          (1) กรณีผู้ได้รับเงินปันผลได้รับเครดิตภาษี โดยจ่ายจาก<br/>
          กำไรสุทธิของกิจการที่ต้องเสียภาษีเงินได้นิติบุคคลในอัตราดังนี้<br/>
          &nbsp;&nbsp;(1.1) อัตราร้อยละ 30 ของกำไรสุทธิ<br/>
          &nbsp;&nbsp;(1.2) อัตราร้อยละ 25 ของกำไรสุทธิ<br/>
          &nbsp;&nbsp;(1.3) อัตราร้อยละ 20 ของกำไรสุทธิ<br/>
          &nbsp;&nbsp;(1.4) อัตราอื่น ๆ (ระบุ).................... ของกำไรสุทธิ<br/>
          (2) กรณีผู้ได้รับเงินปันผลไม่ได้รับเครดิตภาษี เนื่องจากจ่ายจาก<br/>
          &nbsp;&nbsp;(2.1) กำไรสุทธิของกิจการที่ได้รับยกเว้นภาษีเงินได้นิติบุคคล<br/>
          &nbsp;&nbsp;(2.2) เงินปันผลหรือเงินส่วนแบ่งของกำไรที่ได้รับยกเว้นไม่ต้องนำมารวม<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;คำนวณเป็นรายได้เพื่อเสียภาษีเงินได้นิติบุคคล<br/>
          &nbsp;&nbsp;(2.3) กำไรสุทธิส่วนที่ได้หักผลขาดทุนสุทธิยกมาไม่เกิน 5 ปี<br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ก่อนรอบระยะเวลาบัญชีปีปัจจุบัน<br/>
          &nbsp;&nbsp;(2.4) กำไรที่รับรู้ทางบัญชีโดยวิธีส่วนได้เสีย (equity method)<br/>
          &nbsp;&nbsp;(2.5) อื่น ๆ (ระบุ)............................
        </td><td></td><td></td><td></td>
      </tr>
      <tr>
        <td style="font-size:10.5px;">5. การจ่ายเงินได้ที่ต้องหักภาษี ณ ที่จ่าย ตามคำสั่งกรมสรรพากรที่ออกตามมาตรา 3 เตรส เช่น รางวัล ส่วนลดหรือประโยชน์ใด ๆ เนื่องจากการส่งเสริมการขาย รางวัลในการประกวด การแข่งขัน การชิงโชค ค่าแสดงของนักแสดงสาธารณะ ค่าจ้างทำของ ค่าโฆษณา ค่าเช่า ค่าขนส่ง ค่าบริการ ค่าเบี้ยประกันวินาศภัย ฯลฯ</td>
        <td></td><td></td><td></td>
      </tr>
      <tr><td>6. อื่น ๆ (ระบุ).......................................................................</td><td></td><td></td><td></td></tr>
      <tr class="total-row">
        <td style="text-align:center;"><strong>รวมเงินที่จ่ายและภาษีที่หักนำส่ง</strong></td>
        <td></td>
        <td class="r"><strong>${fmtAmt(input.totalIncome)}</strong></td>
        <td class="r"><strong>${fmtAmt(input.totalTax)}</strong></td>
      </tr>
    </tbody>
  </table>

  <!-- Tax in words -->
  <div class="tax-words">
    <strong>รวมเงินภาษีที่หักนำส่ง (ตัวอักษร)</strong> .......................................................
  </div>

  <!-- Fund payments -->
  <div class="fund-row">
    เงินที่จ่ายเข้า กบข./กสจ./กองทุนสงเคราะห์ครูโรงเรียนเอกชน....................บาท
    กองทุนประกันสังคม....................บาท
    กองทุนสำรองเลี้ยงชีพ....................บาท
  </div>

  <!-- Payer method -->
  <div class="payer-row">
    <strong>ผู้จ่ายเงิน</strong>
    <span class="cb"><span class="cb-box cb-checked">&#10003;</span> (1) หัก ณ ที่จ่าย</span>
    <span class="cb"><span class="cb-box"></span> (2) ออกให้ตลอดไป</span>
    <span class="cb"><span class="cb-box"></span> (3) ออกให้ครั้งเดียว</span>
    <span class="cb"><span class="cb-box"></span> (4) อื่น ๆ (ระบุ)............</span>
  </div>

  <!-- Bottom: Warning + Certification -->
  <div class="bottom-section">
    <div class="bottom-left">
      <div class="warning-box">
        <strong>คำเตือน</strong> ผู้มีหน้าที่ออกหนังสือรับรองการหักภาษี ณ ที่จ่าย ฝ่าฝืนไม่ปฏิบัติตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร ต้องรับโทษทางอาญาตามมาตรา 35 แห่งประมวลรัษฎากร
      </div>
    </div>
    <div class="bottom-right">
      <div class="cert-box">
        ขอรับรองว่าข้อความและตัวเลขดังกล่าวข้างต้นถูกต้องตรงกับความจริงทุกประการ
        <div class="sig-line">
          ลงชื่อ.................................................................ผู้จ่ายเงิน
          <div style="margin-top:4px; display:flex; gap:20px; justify-content:center;">
            <span>......../................./${toBE(input.year + 1)}</span>
          </div>
          <div style="text-align:center; font-size:10px;">(วัน เดือน ปี ที่ออกหนังสือรับรองฯ)</div>
        </div>
        <div style="text-align:right; font-size:10px; margin-top:2px;">ประทับตรานิติบุคคล (ถ้ามี)</div>
      </div>
    </div>
  </div>

  <!-- Footnote -->
  <div class="note-section">
    <strong>หมายเหตุ</strong> เลขประจำตัวผู้เสียภาษีอากร (13 หลัก)* หมายถึง
    1. กรณีบุคคลธรรมดาไทย ให้ใช้เลขประจำตัวประชาชนของกรมการปกครอง
    2. กรณีนิติบุคคล ให้ใช้เลขทะเบียนนิติบุคคลของกรมพัฒนาธุรกิจการค้า
    3. กรณีอื่น ๆ นอกเหนือจาก 1. และ 2. ให้ใช้เลขประจำตัวผู้เสียภาษีอากร (13 หลัก) ของกรมสรรพากร
  </div>
</div>
</body>
</html>`

  const w = window.open('', '_blank')
  if (w) {
    w.document.write(html)
    w.document.close()
  }
}
